import React, { useState } from 'react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { formatDate, formatDateTime, formatMonth } from '../../formatters';
import ValidityCounterModal from './ValidityCounterModal';
import CargoCounterModal from './CargoCounterModal';
import CrudeCounterOfferModal from './CrudeCounterOfferModal';

// ─── helpers ────────────────────────────────────────────────────────────────

const cargoHasCounter = c =>
  c &&
  (c.otCounter ||
    c.otOption ||
    c.volumeCounter ||
    c.ddrCounterFrom ||
    c.incotermsCounter?.length);

const cargoCounterStatus = (cargoId, cargoCounters) =>
  cargoHasCounter(cargoCounters[cargoId]) ? 'Counter' : 'No Counter';

const crudeStatus = (crudeId, crudeCounters) => {
  const c = crudeCounters[crudeId];
  if (!c) return 'Not Offered';
  if (c.isCapable) return 'Compliant';
  if (c.alphaDyedCounter != null || c.alphaUndyedCounter != null)
    return 'Counter';
  return 'Not Offered';
};

const CRUDE_STATUS_BADGE = {
  'Not Offered': 'secondary',
  Compliant: 'success',
  Counter: 'warning',
};

const CARGO_STATUS_BADGE = {
  'No Counter': 'secondary',
  Counter: 'warning',
};

// ─── Section 1 ───────────────────────────────────────────────────────────────

const InformationSection = ({ detail }) => {
  const req = detail.requirement;
  const contractPeriod =
    formatMonth(req.startPeriod) && formatMonth(req.endPeriod)
      ? `${formatMonth(req.startPeriod)} – ${formatMonth(req.endPeriod)}`
      : '-';

  return (
    <div className='mb-4'>
      <h6 className='text-uppercase text-muted fw-semibold mb-3 fs-11'>
        Information
      </h6>
      <Row className='g-3'>
        <Col md={6}>
          <Label className='fw-medium text-muted small'>Buyer</Label>
          <Input value='PT Pertamina Patra Niaga' readOnly disabled />
        </Col>
        <Col md={6}>
          <Label className='fw-medium text-muted small'>Seller</Label>
          <Input value={detail.title ?? '-'} readOnly disabled />
        </Col>
        <Col md={6}>
          <Label className='fw-medium text-muted small'>Contract Type</Label>
          <Input value={req.contractType ?? '-'} readOnly disabled />
        </Col>
        <Col md={6}>
          <Label className='fw-medium text-muted small'>Contract Period</Label>
          <Input value={contractPeriod} readOnly disabled />
        </Col>
        <Col md={6}>
          <Label className='fw-medium text-muted small'>
            Submission Deadline (Jakarta Time)
          </Label>
          <Input
            value={formatDateTime(detail.validity?.submissionDeadline)}
            readOnly
            disabled
          />
        </Col>
      </Row>
    </div>
  );
};

// ─── Section 2 ───────────────────────────────────────────────────────────────

const ValidityCounterSection = ({
  detail,
  viewOnly,
  validityCounter,
  onOpenModal,
}) => (
  <div className='mb-4 border-top pt-4'>
    <h6 className='text-uppercase text-muted fw-semibold mb-3 fs-11'>
      Validity Counter
    </h6>
    <Row className='g-3'>
      <Col md={6}>
        <Label className='fw-medium text-muted small'>
          Validity Period (Jakarta Time)
        </Label>
        <Input
          value={formatDateTime(detail.validity?.validityPeriod)}
          readOnly
          disabled
        />
      </Col>
      <Col md={6}>
        <Label className='fw-medium text-muted small d-flex align-items-center gap-2'>
          Validity Period Counter (Jakarta Time)
          {!viewOnly && (
            <ActionButton
              variant='icon'
              size='sm'
              icon='ri-edit-line'
              label='Edit Validity Period Counter'
              onClick={onOpenModal}
            />
          )}
        </Label>
        <Input
          value={validityCounter ? formatDateTime(validityCounter) : 'N/A'}
          readOnly
          disabled
          className={validityCounter ? 'bg-warning-subtle' : ''}
        />
      </Col>
    </Row>
  </div>
);

// ─── Section 3 — Crude Detail Counter ─────────────────────────────────────────

const CrudeDetailCounter = ({
  detail,
  viewOnly,
  cargoCounters,
  onOpenCargoModal,
  crudeCounters,
  onCrudeCapableToggle,
  onCrudeAlphaChange,
  onOpenCrudeModal,
}) => {
  const [expandedCrudes, setExpandedCrudes] = useState({});

  const toggleExpand = crudeId =>
    setExpandedCrudes(prev => ({ ...prev, [crudeId]: !prev[crudeId] }));

  const cargos = detail.requirement?.cargos ?? [];

  return (
    <div className='mb-4 border-top pt-4'>
      <h6 className='text-uppercase text-muted fw-semibold mb-3 fs-11'>
        Crude Detail Counter
      </h6>
      {cargos.map((cargo, idx) => {
        const cc = cargoCounters[cargo.id] ?? {};
        const cStatus = cargoCounterStatus(cargo.id, cargoCounters);

        return (
          <Card className='border mb-3' key={cargo.id}>
            <CardHeader className='d-flex align-items-center justify-content-between bg-light-subtle py-2'>
              <span className='fw-semibold'>
                Cargo {idx + 1} — {cargo.plant} – {cargo.unit}
              </span>
              <div className='d-flex align-items-center gap-2'>
                <Badge color={CARGO_STATUS_BADGE[cStatus]}>{cStatus}</Badge>
                {!viewOnly && (
                  <ActionButton
                    variant='icon'
                    size='sm'
                    icon='ri-edit-line'
                    label='Edit Counter'
                    onClick={() => onOpenCargoModal(cargo.id)}
                  />
                )}
              </div>
            </CardHeader>
            <CardBody className='pb-2'>
              {/* Cargo header info grid */}
              <div className='table-responsive mb-3'>
                <Table bordered size='sm' className='mb-0 align-middle'>
                  <thead className='table-light'>
                    <tr>
                      <th>Field</th>
                      <th>Planning Value</th>
                      <th>Counter</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='text-muted small'>Type</td>
                      <td>{cargo.cargoType ?? '-'}</td>
                      <td>—</td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>OT</td>
                      <td>{cargo.ot != null ? `${cargo.ot}%` : '-'}</td>
                      <td>
                        {cc.otCounter ? (
                          <span className='text-warning fw-medium'>
                            {cc.otCounter}
                          </span>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>OT Option</td>
                      <td>—</td>
                      <td>
                        {cc.otOption ? (
                          <span className='text-warning fw-medium'>
                            {cc.otOption}
                          </span>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>Volume</td>
                      <td>
                        {cargo.volume != null
                          ? cargo.volume.toLocaleString('en-US')
                          : '-'}
                      </td>
                      <td>
                        {cc.volumeCounter ? (
                          <span className='text-warning fw-medium'>
                            {Number(cc.volumeCounter).toLocaleString('en-US')}
                          </span>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>DDR</td>
                      <td>
                        {cargo.ddrFrom && cargo.ddrTo
                          ? `${formatDate(cargo.ddrFrom)} – ${formatDate(cargo.ddrTo)}`
                          : '-'}
                      </td>
                      <td>
                        {cc.ddrCounterFrom ? (
                          <span className='text-warning fw-medium'>
                            {formatDate(cc.ddrCounterFrom)}
                            {cc.ddrCounterTo
                              ? ` – ${formatDate(cc.ddrCounterTo)}`
                              : ''}
                          </span>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>Incoterms</td>
                      <td>
                        {cargo.incoterms?.length
                          ? cargo.incoterms.join(', ')
                          : '-'}
                      </td>
                      <td>
                        {cc.incotermsCounter?.length ? (
                          <span className='text-warning fw-medium'>
                            {cc.incotermsCounter.join(', ')}
                          </span>
                        ) : (
                          <span className='text-muted'>N/A</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted small'>Tujuan</td>
                      <td>
                        {cargo.plant && cargo.unit
                          ? `${cargo.plant} - ${cargo.unit}`
                          : '-'}
                      </td>
                      <td>—</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Crude/Condensat table */}
              <h6 className='text-muted small fw-semibold mb-2'>
                Crude / Condensat
              </h6>
              <div className='table-responsive'>
                <Table bordered size='sm' className='align-middle mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th></th>
                      <th>Crude/Condensat</th>
                      <th>Max Capacity</th>
                      <th>Klasifikasi</th>
                      <th>Remark Max Capacity</th>
                      <th>Is Via CIB Only?</th>
                      <th>% Bleeding (1)</th>
                      <th>% Bleeding (2)</th>
                      <th>Is Capable?</th>
                      <th>Status</th>
                      <th>Alpha Dyed</th>
                      <th>Alpha Undyed</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cargo.crudes ?? []).map(cr => {
                      const crC = crudeCounters[cr.id] ?? {};
                      const status = crudeStatus(cr.id, crudeCounters);
                      const isCapable = crC.isCapable ?? false;
                      const isExpanded = !!expandedCrudes[cr.id];
                      const hasCounterData =
                        crC.alphaDyedCounter != null ||
                        crC.alphaUndyedCounter != null;

                      return (
                        <React.Fragment key={cr.id}>
                          <tr>
                            <td>
                              <button
                                type='button'
                                className='btn btn-sm btn-link p-0 text-muted'
                                onClick={() => toggleExpand(cr.id)}
                                title={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                <i
                                  className={
                                    isExpanded
                                      ? 'ri-arrow-up-s-line'
                                      : 'ri-arrow-down-s-line'
                                  }
                                />
                              </button>
                            </td>
                            <td className='fw-medium'>{cr.crudeName ?? '-'}</td>
                            <td>
                              {cr.maxCapacity != null
                                ? cr.maxCapacity.toLocaleString('en-US')
                                : '-'}
                            </td>
                            <td>{cr.klasifikasi ?? '—'}</td>
                            <td>{cr.remarkMaxCapacity || '—'}</td>
                            <td>
                              <Badge
                                color={cr.isViaCibOnly ? 'info' : 'light'}
                                className='text-dark'
                              >
                                {cr.isViaCibOnly ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              {cr.bleeding1 != null ? `${cr.bleeding1}%` : '—'}
                            </td>
                            <td>
                              {cr.bleeding2 != null ? `${cr.bleeding2}%` : '—'}
                            </td>
                            <td>
                              <div className='form-check form-switch d-flex justify-content-center'>
                                <Input
                                  type='checkbox'
                                  role='switch'
                                  className='form-check-input'
                                  checked={isCapable}
                                  disabled={viewOnly}
                                  onChange={() => onCrudeCapableToggle(cr.id)}
                                />
                              </div>
                            </td>
                            <td>
                              <Badge color={CRUDE_STATUS_BADGE[status]}>
                                {status}
                              </Badge>
                            </td>
                            <td>
                              <Input
                                type='number'
                                min={0}
                                bsSize='sm'
                                value={crC.alphaDyed ?? ''}
                                disabled={!isCapable || viewOnly}
                                onChange={e =>
                                  onCrudeAlphaChange(
                                    cr.id,
                                    'alphaDyed',
                                    e.target.value
                                  )
                                }
                                style={{ width: 90 }}
                              />
                            </td>
                            <td>
                              <Input
                                type='number'
                                min={0}
                                bsSize='sm'
                                value={crC.alphaUndyed ?? ''}
                                disabled={!isCapable || viewOnly}
                                onChange={e =>
                                  onCrudeAlphaChange(
                                    cr.id,
                                    'alphaUndyed',
                                    e.target.value
                                  )
                                }
                                style={{ width: 90 }}
                              />
                            </td>
                            <td>
                              {!isCapable && (
                                <ActionButton
                                  variant='secondary'
                                  size='sm'
                                  icon='ri-exchange-line'
                                  disabled={viewOnly}
                                  onClick={() => onOpenCrudeModal(cr.id)}
                                >
                                  Counter Offer
                                </ActionButton>
                              )}
                            </td>
                          </tr>
                          {/* Expandable row */}
                          <tr className={isExpanded ? '' : 'd-none'}>
                            <td colSpan={13} className='bg-light-subtle p-3'>
                              <Collapse isOpen={isExpanded}>
                                {hasCounterData ? (
                                  <div className='d-flex gap-4'>
                                    <div>
                                      <span className='text-muted small'>
                                        Alpha Dyed Counter:{' '}
                                      </span>
                                      <strong>
                                        {crC.alphaDyedCounter ?? '—'}
                                      </strong>
                                    </div>
                                    <div>
                                      <span className='text-muted small'>
                                        Alpha Undyed Counter:{' '}
                                      </span>
                                      <strong>
                                        {crC.alphaUndyedCounter ?? '—'}
                                      </strong>
                                    </div>
                                  </div>
                                ) : (
                                  <p className='text-muted mb-0 fst-italic'>
                                    There is no counter offer
                                  </p>
                                )}
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

// ─── Section 4 — Klausul Counter ─────────────────────────────────────────────

const KlausulCounter = ({
  clauses,
  viewOnly,
  clauseResponses,
  onClauseRespond,
  onClauseResponseChange,
  onClauseResponseSubmit,
  onClauseResponseCancel,
}) => {
  const [openClauses, setOpenClauses] = useState({});

  const toggleClause = id =>
    setOpenClauses(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className='mb-4 border-top pt-4'>
      <h6 className='text-uppercase text-muted fw-semibold mb-3 fs-11'>
        Klausul Counter
      </h6>
      {(clauses ?? []).map((c, idx) => {
        const resp = clauseResponses[c.clauseId] ?? {};
        const isOpen = !!openClauses[c.clauseId];

        return (
          <Card className='border mb-2' key={c.clauseId}>
            <CardHeader
              className='d-flex align-items-center justify-content-between bg-light-subtle py-2 cursor-pointer'
              onClick={() => toggleClause(c.clauseId)}
              style={{ cursor: 'pointer' }}
            >
              <span className='fw-semibold'>
                {idx + 1}. {c.title}
              </span>
              <i
                className={
                  isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                }
              />
            </CardHeader>
            <Collapse isOpen={isOpen}>
              <CardBody>
                {/* Mandatory toggle */}
                <div className='d-flex align-items-center gap-2 mb-3'>
                  <Input
                    type='checkbox'
                    role='switch'
                    className='form-check-input'
                    checked={!!c.mandatory}
                    disabled
                    readOnly
                  />
                  <Label className='form-check-label mb-0 text-muted small'>
                    Mandatory clause for Registered Supplier to fulfill
                  </Label>
                </div>

                {/* Summernote display */}
                <div
                  className='p-3 border rounded bg-light-subtle mb-3'
                  dangerouslySetInnerHTML={{ __html: c.summernote }}
                />

                {/* Respond button */}
                {!viewOnly && !resp.expanded && (
                  <ActionButton
                    variant='secondary'
                    size='sm'
                    icon='ri-reply-line'
                    onClick={() => onClauseRespond(c.clauseId)}
                  >
                    Respond
                  </ActionButton>
                )}

                {/* Response card */}
                {resp.expanded && (
                  <div className='border rounded p-3 bg-white mt-2'>
                    <Label className='fw-medium small mb-1'>Respon Anda</Label>
                    <Input
                      type='textarea'
                      rows={4}
                      value={resp.responseText ?? ''}
                      onChange={e =>
                        onClauseResponseChange(c.clauseId, e.target.value)
                      }
                      placeholder='Tuliskan respon terhadap klausul ini...'
                    />
                    <div className='d-flex gap-2 mt-2'>
                      <ActionButton
                        variant='secondary'
                        size='sm'
                        icon='ri-close-line'
                        onClick={() => onClauseResponseCancel(c.clauseId)}
                      >
                        Cancel
                      </ActionButton>
                      <ActionButton
                        variant='primary'
                        size='sm'
                        icon='ri-send-plane-line'
                        onClick={() => onClauseResponseSubmit(c.clauseId)}
                      >
                        Submit
                      </ActionButton>
                    </div>
                  </div>
                )}

                {/* Show submitted response */}
                {!resp.expanded && resp.responseText && (
                  <div className='border rounded p-3 bg-success-subtle mt-2'>
                    <p className='text-muted small mb-1 fw-medium'>
                      Respon Anda:
                    </p>
                    <p className='mb-0'>{resp.responseText}</p>
                    {!viewOnly && (
                      <ActionButton
                        variant='tertiary'
                        size='sm'
                        className='p-0 mt-1'
                        icon='ri-edit-line'
                        onClick={() => onClauseRespond(c.clauseId)}
                      >
                        Edit Respon
                      </ActionButton>
                    )}
                  </div>
                )}
              </CardBody>
            </Collapse>
          </Card>
        );
      })}
    </div>
  );
};

// ─── Main OfferingTab ─────────────────────────────────────────────────────────

const OfferingTab = ({
  detail,
  viewOnly,
  validityCounter,
  cargoCounters,
  crudeCounters,
  clauseResponses,
  onClauseRespond,
  onClauseResponseChange,
  onClauseResponseSubmit,
  onClauseResponseCancel,
  onCrudeCapableToggle,
  onCrudeAlphaChange,
  onOpenCrudeModal,
  onCargoCounterSubmit,
  onValidityCounterSubmit,
}) => {
  const [validityModalOpen, setValidityModalOpen] = useState(false);
  const [cargoModalId, setCargoModalId] = useState(null);
  const [crudeModalId, setCrudeModalId] = useState(null);

  const activeCargo =
    cargoModalId != null
      ? (detail.requirement?.cargos ?? []).find(c => c.id === cargoModalId)
      : null;

  const activeCrude =
    crudeModalId != null
      ? (detail.requirement?.cargos ?? [])
          .flatMap(c => c.crudes ?? [])
          .find(cr => cr.id === crudeModalId)
      : null;

  return (
    <div className='p-3'>
      {/* Section 1 */}
      <InformationSection detail={detail} />

      {/* Section 2 */}
      <ValidityCounterSection
        detail={detail}
        viewOnly={viewOnly}
        validityCounter={validityCounter}
        onOpenModal={() => setValidityModalOpen(true)}
      />

      {/* Section 3 */}
      <CrudeDetailCounter
        detail={detail}
        viewOnly={viewOnly}
        cargoCounters={cargoCounters}
        onOpenCargoModal={id => setCargoModalId(id)}
        crudeCounters={crudeCounters}
        onCrudeCapableToggle={onCrudeCapableToggle}
        onCrudeAlphaChange={onCrudeAlphaChange}
        onOpenCrudeModal={id => setCrudeModalId(id)}
      />

      {/* Section 4 */}
      <KlausulCounter
        clauses={detail.requirement?.clauses}
        viewOnly={viewOnly}
        clauseResponses={clauseResponses}
        onClauseRespond={onClauseRespond}
        onClauseResponseChange={onClauseResponseChange}
        onClauseResponseSubmit={onClauseResponseSubmit}
        onClauseResponseCancel={onClauseResponseCancel}
      />

      {/* Modals */}
      <ValidityCounterModal
        isOpen={validityModalOpen}
        onClose={() => setValidityModalOpen(false)}
        currentValue={validityCounter}
        onSubmit={val => {
          onValidityCounterSubmit(val);
          setValidityModalOpen(false);
        }}
      />

      <CargoCounterModal
        isOpen={cargoModalId != null}
        cargo={activeCargo}
        currentCounter={cargoModalId ? cargoCounters[cargoModalId] : null}
        onClose={() => setCargoModalId(null)}
        onSubmit={form => {
          onCargoCounterSubmit(cargoModalId, form);
          setCargoModalId(null);
        }}
      />

      <CrudeCounterOfferModal
        isOpen={crudeModalId != null}
        crude={activeCrude}
        onClose={() => setCrudeModalId(null)}
        onSubmit={form => {
          onOpenCrudeModal(crudeModalId, form);
          setCrudeModalId(null);
        }}
      />
    </div>
  );
};

export default OfferingTab;
